import { WSTimelineItemProps } from "@wingspanhq/fe-component-library";
import { PayrollRun } from "@wingspanhq/wingspan-api-sdk";
import orderBy from "lodash/orderBy";

/**
 * Builds the "Recent history" timeline from a run's lifecycle timestamps.
 *
 * `PayrollRunEvents` is the only history the v3 API exposes today — the generic
 * resource-events feed is still pending (WIN-15770), so activity actors and
 * free-form events are not available yet. Every entry here is real data: an
 * event with no timestamp simply hasn't happened and is omitted.
 */
const EVENT_LABELS: Array<{
  key: keyof NonNullable<PayrollRun["events"]>;
  content: string;
  pill: WSTimelineItemProps["pills"];
}> = [
  {
    key: "draftAt",
    content: "Payroll run opened",
    pill: [{ text: "Open", theme: "neutral" }]
  },
  {
    key: "pendingApprovalsAt",
    content: "Moved to pending approvals",
    pill: [{ text: "Pending", theme: "neutral" }]
  },
  {
    key: "finalizedAt",
    content: "Payroll finalized",
    pill: [{ text: "Finalized", theme: "blue" }]
  },
  {
    key: "processingAt",
    content: "Payroll processing",
    pill: [{ text: "Processing", theme: "blue" }]
  },
  {
    key: "paidAt",
    content: "Payroll paid",
    pill: [{ text: "Paid", theme: "success" }]
  },
  {
    key: "cancelledAt",
    content: "Payroll cancelled",
    pill: [{ text: "Cancelled", theme: "neutral" }]
  },
  {
    key: "failedAt",
    content: "Payroll failed",
    pill: [{ text: "Failed", theme: "error" }]
  }
];

export const selectorPayrollRunHistoryItems = ({
  payrollRuns,
  limit
}: {
  payrollRuns: PayrollRun[];
  /** Cap for the dashboard sidebar, which aggregates across runs. */
  limit?: number;
}): WSTimelineItemProps[] => {
  const items = payrollRuns.flatMap(payrollRun =>
    EVENT_LABELS.flatMap(event => {
      const date = payrollRun.events?.[event.key];

      if (!date) {
        return [];
      }

      return [
        {
          date,
          content: payrollRun.payrollNumber
            ? `${event.content} · #${payrollRun.payrollNumber}`
            : event.content,
          pills: event.pill
        }
      ];
    })
  );

  const sorted = orderBy(items, item => item.date, "desc");

  return limit === undefined ? sorted : sorted.slice(0, limit);
};
